import { type FC, type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/pro-regular-svg-icons/faSliders';
import { Button } from 'Button';
import { Capsule } from 'Capsule';
import { Drawer } from 'Drawer';
import { Text } from 'Text';
import { type BrowseFilters, type ProgressStatus, type StatusOption } from '../BrowseCourses.types';
import { SELECTED_CAPSULE_CLASS_NAMES, UNSELECTED_CAPSULE_CLASS_NAMES } from '../constants';
import { useHorizontalScrollFade } from '../hooks/useHorizontalScrollFade';
import '../locales/browseCourses/browseCourses-en.json';

type Props = {
    filters: BrowseFilters;
    isOpen: boolean;
    onOpen: () => void;
    onSave: () => void;
    onCancel: () => void;
    // Each pill's × removes that one filter
    onRemoveTopic: (topic: string) => void;
    onRemoveProgress: (progress: ProgressStatus) => void;
    onRemoveStatus: (status: StatusOption) => void;
    children: ReactNode;
};

// Below the sm breakpoint the filters live in a drawer. Changes apply when the user saves, and closing
// the drawer any other way discards them.
export const MobileFilters: FC<Props> = ({
    filters,
    isOpen,
    onOpen,
    onSave,
    onCancel,
    onRemoveTopic,
    onRemoveProgress,
    onRemoveStatus,
    children,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });

    // Every active filter. The strip scrolls sideways when the pills overflow, so it never wraps.
    const { selectedStatus } = filters;
    const summary = [
        ...filters.topics.map(topic => ({ key: `topic-${topic}`, label: topic, onRemove: () => onRemoveTopic(topic) })),
        ...filters.progress.map(progress => ({
            key: `progress-${progress}`,
            label: t(progress),
            onRemove: () => onRemoveProgress(progress),
        })),
        ...(selectedStatus === 'any_status'
            ? []
            : [{ key: 'status', label: t(selectedStatus), onRemove: () => onRemoveStatus(selectedStatus) }]),
    ];
    const stripRef = useRef<HTMLDivElement>(null);
    const fadeStyle = useHorizontalScrollFade(stripRef, summary.map(item => item.key).join('|'));

    return (
        <div className="sm:hidden">
            <div className="flex items-center gap-2">
                <Button
                    className="shrink-0"
                    variant="bordered-white"
                    color="slate-grey"
                    height={8}
                    icon={<FontAwesomeIcon icon={faSliders} />}
                    onPress={onOpen}
                >
                    {t('setFiltersTitle')}
                </Button>
                {/* -me-3.5 and pe-3.5 run the strip to the screen edge, past the Container's px-3.5 */}
                <div
                    ref={stripRef}
                    style={fadeStyle}
                    className="-me-3.5 flex min-w-0 items-center gap-1.5 overflow-x-auto pe-3.5 scrollbar-hide"
                >
                    {summary.length === 0 ? (
                        <Capsule size="sm" classNames={UNSELECTED_CAPSULE_CLASS_NAMES}>
                            {t('allCourses')}
                        </Capsule>
                    ) : (
                        summary.map(item => (
                            <Capsule
                                key={item.key}
                                size="sm"
                                classNames={SELECTED_CAPSULE_CLASS_NAMES}
                                onClose={item.onRemove}
                            >
                                {item.label}
                            </Capsule>
                        ))
                    )}
                </div>
            </div>

            <Drawer
                isOpen={isOpen}
                onClose={onCancel}
                maxHeight="80vh"
                scrollFade
                header={<Text as="h2">{t('setFiltersTitle')}</Text>}
                footer={
                    <Button color="black" fullWidth onPress={onSave}>
                        {t('saveAndClose')}
                    </Button>
                }
            >
                {children}
            </Drawer>
        </div>
    );
};
